import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { formatDate } from '../../../utils/date'
import Image from '@components/ui/Image'
import { getFullAttachmentUrl } from '@services/api/index.js'
const AchievementCard = ({ 
  achievement, 
  onClick,
  className = "",
  variant = "default" // default, compact
}) => {
  const { t } = useTranslation()
  
  const getCategoryIcon = (category) => {
    const icons = {
      'project': '🏗️',
      'research': '📊',
      'cooperation': '🤝',
      'training': '📚',
      'exhibition': '🎨',
      'publication': '📖',
      'event': '🎯'
    }
    return icons[category] || '📋'
  }

  const getTypeColor = (type) => {
    const colors = {
      'image_text': 'bg-blue-100 text-blue-800',
      'mixed': 'bg-green-100 text-green-800',
      'interactive': 'bg-purple-100 text-purple-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const getTypeText = (type) => {
    const types = {
      'image_text': t('achievements.types.image_text'),
      'mixed': t('achievements.types.mixed'),
      'interactive': t('achievements.types.interactive')
    }
    return types[type] || type
  }

  const formatParticipants = (participants) => {
    if (!Array.isArray(participants) || participants.length === 0) return ''
    
    if (participants.length <= 2) {
      return participants.join('、')
    }
    return `${participants.slice(0, 2).join('、')}等`
  }

  const handleCardClick = () => {
    if (onClick) {
      onClick(achievement.resultId)
    }
  }

  return (
    <div 
      className={`achievement-card bg-white rounded-lg overflow-hidden shadow-sm
                 border border-gray-200 hover:shadow-lg
                 transition-all duration-300 cursor-pointer
                 hover:border-primary-300 hover:-translate-y-1 ${className}`}
      onClick={handleCardClick}
    >
      {/* 成果封面 */}
      <div className="achievement-cover-container relative">
        {achievement.coverId ? (
          <img
            src={getFullAttachmentUrl(achievement.coverId)}
            alt={achievement.title}
            className="w-full aspect-[16/9] object-cover bg-gray-100"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/placeholders/achievement-placeholder.jpg';
            }}
          />
        ) : (
          <img
            src="/images/placeholders/achievement-placeholder.jpg"
            alt={achievement.title}
            className="w-full aspect-[16/9] object-cover bg-gray-100"
            loading="lazy"
          />
        )}
        
        {/* 成果类型标识 */}
        {achievement.type && (
          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(achievement.type)}`}>
            {getTypeText(achievement.type)}
          </div>
        )}
      </div>

      {/* 成果信息 */}
      <div className="achievement-info p-4 space-y-3">
        {/* 成果标题 */}
        <h3 className="achievement-title font-semibold text-gray-900 line-clamp-2 text-sm leading-5">
          {achievement.title}
        </h3>

        {/* 成果描述 */}
        {achievement.summary && (
          <p className="achievement-description text-xs text-gray-500 line-clamp-2 leading-4">
            {achievement.summary}
          </p>
        )}

        {/* 成果标签 */}
        {achievement.tags && achievement.tags.length > 0 && (
          <div className="achievement-tags flex flex-wrap gap-1">
            {achievement.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="achievement-tag px-2 py-1 bg-gray-100 text-gray-600 
                                        rounded text-xs font-medium">
                [{tag}]
              </span>
            ))}
            {achievement.tags.length > 2 && (
              <span className="text-xs text-gray-400">+{achievement.tags.length - 2}</span>
            )}
          </div>
        )}

        {/* 展示方式和操作区域 */}
        <div className="achievement-actions flex justify-between items-center mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center text-xs text-gray-500">
            {achievement.type === 'image_text' && (
              <span className="flex items-center">
                <span className="mr-1">📊</span>
                {t('achievements.card.imageText')}
              </span>
            )}
            {achievement.type === 'mixed' && (
              <span className="flex items-center">
                <span className="mr-1">📷</span>
                {t('achievements.card.mixed')}
              </span>
            )}
          </div>
          
          <Link 
            to={`/achievements/${achievement.resultId}`}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {t('common.viewDetail')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default AchievementCard 